import React from 'react';
import { CheckIcon, ChevronDownIcon, ExternalLinkIcon } from 'lucide-react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { isEmpty } from 'lodash';
import { useFormikContext } from 'formik';

import { Alert, AlertDescription } from '@dashboard/components/ui/alert';
import Button from '@dashboard/components/Button';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import { Badge } from '@dashboard/components/ui/badge';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { formatPriceForInput, formatPrice } from '@dashboard/services/formatting/price';
import getSubscriptionIntervalWithSlash from '@dashboard/services/formatting/getSubscriptionIntervalWithSlash';
import { fetcher } from '@dashboard/services/request';
import { isProduction } from '@dashboard/services/env';
import { sellerPlansKey } from '@dashboard/lib/queryKeys';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';

/**
 * Per-price Stripe plan picker. Choosing an existing Stripe recurring price
 * marks the price managed-in-Stripe and fills amount / currency / interval from
 * it (those fields become read-only in the form, and Stripe-price sync is
 * skipped for the entry). `planId` is the price's current stripePriceId.
 */
export default function PricePlanSelector({ fieldPrefix, planId }) {
  const { sellerId, token, seller, isAdminLoggedInAsSeller } = useAuth();
  const { setFieldValue } = useFormikContext();

  const field = name => (fieldPrefix ? `${fieldPrefix}.${name}` : name);

  const { data: plans, isLoading: isValidating } = useQuery({
    queryKey: sellerPlansKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}/plans`, token),
  });

  // Derive the selected plan from the price's stripePriceId — no synced state.
  const selectedPlan = !isEmpty(plans) && planId ? plans.find(p => p.id === planId) : undefined;

  // Selecting a plan is a user action, so fill the price fields here (not in an
  // effect). The amount/currency/interval inputs are disabled while managed.
  const onSelectPlan = plan => {
    if (!plan) return;
    setFieldValue(field('managedInStripe'), true);
    setFieldValue(field('stripePriceId'), plan.id);
    setFieldValue(
      field('amount'),
      formatPriceForInput({ amount: plan.amount, currency: plan.currency, style: 'decimal' }),
    );
    setFieldValue(field('currency'), plan.currency);
    setFieldValue(field('interval'), plan.interval);
    setFieldValue(field('intervalCount'), plan.interval_count || 1);
    if (plan.trial_period_days) {
      setFieldValue(field('trialPeriodDays'), plan.trial_period_days);
    }
  };

  return (
    <div>
      <Listbox value={planId} onChange={onSelectPlan}>
        <ListboxButton
          className="flex h-auto min-h-9 w-full items-center justify-between gap-x-2 py-1.5 text-left whitespace-normal"
          as={Button}
          type="button"
          appearance="light"
        >
          {selectedPlan ? (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {selectedPlan.product.name}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {formatPrice({
                  amount: selectedPlan.amount,
                  currency: selectedPlan.currency,
                  style: 'currency',
                })}{' '}
                {getSubscriptionIntervalWithSlash(
                  selectedPlan.interval,
                  selectedPlan.interval_count,
                )}
                {selectedPlan.trial_period_days && ` • ${selectedPlan.trial_period_days} day trial`}
              </span>
              <span className="mt-1 flex flex-wrap gap-1">
                <Badge variant="outline">{selectedPlan.product.id}</Badge>
                <Badge variant="outline">{selectedPlan.id}</Badge>
              </span>
            </span>
          ) : (
            <span className="flex-1 text-muted-foreground">Select price</span>
          )}

          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        </ListboxButton>
        <ListboxOptions
          anchor={{ to: 'bottom start', gap: 8 }}
          className="z-50 w-[var(--button-width)] [--anchor-max-height:32rem] overflow-auto rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-md focus:outline-none"
        >
          {isEmpty(plans) && !isValidating && (
            <ListboxOption className="p-2">
              No subscription prices found in your Stripe account
            </ListboxOption>
          )}

          {isValidating &&
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                aria-hidden="true"
                className="flex w-full items-center gap-x-2 rounded-md p-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex h-5 items-center">
                    <Skeleton className="h-3.5 w-32" />
                  </div>
                  <div className="flex h-4 items-center">
                    <Skeleton className="h-3 w-44" />
                  </div>
                  <div className="mt-1 flex gap-1">
                    <Skeleton className="h-5 w-16 rounded-md" />
                    <Skeleton className="h-5 w-28 rounded-md" />
                  </div>
                </div>
                <Skeleton className="size-7 shrink-0 rounded-md" />
              </div>
            ))}

          {plans?.map(plan => (
            <ListboxOption
              key={plan.id}
              value={plan}
              className={clsx('flex w-full items-center gap-x-2 rounded-md p-2 text-left', {
                'bg-accent text-accent-foreground': plan.id === planId,
                'hover:bg-accent hover:text-accent-foreground': plan.id !== planId,
              })}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{plan.product.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {formatPrice({ amount: plan.amount, currency: plan.currency, style: 'currency' })}{' '}
                  {getSubscriptionIntervalWithSlash(plan.interval, plan.interval_count)}
                  {plan.trial_period_days && ` • ${plan.trial_period_days} day trial`}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  <Badge variant="outline">{plan.product.id}</Badge>
                  <Badge variant="outline">{plan.id}</Badge>
                </div>
              </div>

              <div className="flex shrink-0 gap-x-2 ml-auto items-center">
                <div className="flex size-4 items-center justify-center">
                  {planId === plan.id && <CheckIcon className="size-4" />}
                </div>

                <Button
                  to={getStripeDashboardUrl(`/prices/${plan.id}`, {
                    seller,
                    isAdminLoggedInAsSeller,
                  })}
                  target="_blank"
                  appearance="icon-button"
                  onClick={e => e.stopPropagation()}
                >
                  <ExternalLinkIcon className="size-4" />
                </Button>
              </div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>

      {selectedPlan && !selectedPlan.livemode && isProduction && (
        <Alert className="my-2">
          <AlertDescription>
            We no longer support Stripe test prices for checkouts. Select another price or turn off
            “Use Stripe price”.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
